import comm from "csi/comm";
import GetRequest from "csi/comm/GetRequest";
import Key from "csi/core/Key";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { getNodeClass } from "csi/reflection/reflection";
import reflectionHints from "csi/reflectionHints";
import { C8AttributeId, C8ImageMap, C8Node, cnl } from "csi/typings";
import { FORMAT_DATETIME, formatTime } from "csi/utility/dateUtility";
import { formatFileSize, getFileNameExtension } from "csi/utility/fileUtility";
import { PATH_OP_INDEX, pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import _ from "lodash";

export const SMALLEST_SIZE = 64;
export const SMALL_SIZE = 128;
export const MEDIUM_SIZE = 256;
export const LARGE_SIZE = 384;
export const LARGEST_SIZE = 512;
export const MAX_VIEWABLE_IMAGE_SIZE = 16383;

// FIX: should lookup these numbers from "centric://REFLECTION/INSTANCE/DocumentManager/Instance" - need to cache this node during Update Configuration
export const LARGE_IMAGE_SIZE = 1536;
export const SMALL_IMAGE_SIZE = 512;
export const THUMBNAIL_IMAGE_SIZE = 128;

// The total pixel count of an image should be over 2048 in order to progressively load it
export const MIN_PROGRESSIVE_IMG_PIXEL_CT = 2048;

// Allowed aspect ratio of images is 1.5, meaning width can be 1.5 times larger then height
export const ASPECT_RATIO = 1.5;

// Enumeration values for the image size
export const IMAGE_SIZE_ICON = "ImageSize:Icon";
export const IMAGE_SIZE_SMALLEST = "ImageSize:Smallest";
export const IMAGE_SIZE_SMALL = "ImageSize:Small";
export const IMAGE_SIZE_MEDIUM = "ImageSize:Medium";
export const IMAGE_SIZE_LARGE = "ImageSize:Large";
export const IMAGE_SIZE_LARGEST = "ImageSize:Largest";
export const IMAGE_SIZE_ORIGINAL = "ImageSize:Original";
export const IMAGE_SIZE_USE_LARGE_IMAGE = "ImageSize:UseLargeImage";
export const IMAGE_SIZE_CUSTOM = "ImageSize:Custom";

export const ATTR_IMAGES = "Images";
export const ATTR_THUMBNAIL = "Thumbnail";
export const ATTR_VIEWABLE = "Viewable";
export const ATTR_VIEWABLE_GALLERY = "ViewableGallery";
export const ATTR_MEDIUM_IMAGE = "SmallImage";
export const ATTR_MEDIUM_GALLERY = "SmallGallery";
export const ATTR_LARGE_IMAGE = "LargeImage";
export const ATTR_LARGE_GALLERY = "LargeGallery";
export const DEFAULT_IMAGE_KEY = "-1";
export const DEFAULT_IMAGE_LABEL = "";
export const DEFAULT_IMAGE_PATH = pathCreate(ATTR_IMAGES, PATH_OP_INDEX, DEFAULT_IMAGE_LABEL);
export const TYPE_IMAGE = "Image";
export const GALLERY_TYPE_TURNTABLE = "ImageGalleryType:Turntable";

export const SIZE_ATTRIBUTE = {
	[IMAGE_SIZE_ICON]: ATTR_THUMBNAIL,
	[IMAGE_SIZE_SMALLEST]: ATTR_THUMBNAIL,
	[IMAGE_SIZE_SMALL]: ATTR_THUMBNAIL,
	[IMAGE_SIZE_MEDIUM]: ATTR_MEDIUM_IMAGE,
	[IMAGE_SIZE_LARGE]: ATTR_MEDIUM_IMAGE,
	[IMAGE_SIZE_LARGEST]: ATTR_MEDIUM_IMAGE,
	[IMAGE_SIZE_ORIGINAL]: ATTR_VIEWABLE,
	[IMAGE_SIZE_USE_LARGE_IMAGE]: ATTR_LARGE_IMAGE,
};

const MAX_HEIGHT = {
	[IMAGE_SIZE_SMALLEST]: SMALLEST_SIZE,
	[IMAGE_SIZE_SMALL]: SMALL_SIZE,
	[IMAGE_SIZE_MEDIUM]: MEDIUM_SIZE,
	[IMAGE_SIZE_LARGE]: LARGE_SIZE,
	[IMAGE_SIZE_LARGEST]: LARGEST_SIZE,
};

export interface ImageDimensions {
	/** Height of the image */
	readonly height: number;

	/** Width of the image */
	readonly width: number;
}

export interface ImageData extends ImageDimensions {
	/** attribute referencing image */
	readonly containerAttributeId?: C8AttributeId;

	/** url of node referencing image */
	readonly containerUrl?: cnl;

	/** Function to call to get new image data if/when the image is edited and replaced */
	readonly copyTo?: (newImageNode: C8Node, newImageContainerNode: C8Node) => ImageData;

	/** Creation date the image */
	readonly dateCreated: string;

	/** File vault url for viewable image */
	readonly downloadUrl?: cnl;

	/** Indicates if image attributes can be edited (default false) */
	readonly editableAttributes?: boolean;

	/** key of image in refmap attribute to edit (in cases other than ImageContainer.Images) */
	readonly editableAttributeKey?: string;

	/** Indicates if image can be edited (default false) */
	readonly editableImage?: boolean;

	/** numbered key of the image in image container map */
	readonly imageKey: string;

	/** internal (non-localized) image labels applied to image */
	readonly imageLabels: string[];

	/** File size of the image */
	readonly imageSize: string;

	/** Type of the image (extension) */
	readonly imageType: string;

	/** Indicates if this is the default image in its current context */
	readonly isDefault?: boolean;

	/** Flag used for telling image is turntable */
	readonly isTurntable?: boolean;

	/** Source of the turntable images */
	readonly lightboxGallerySrc?: string[];

	/** Viewable source of the image (to display in lightbox) */
	readonly lightboxSrc: string;

	/** Name of the image */
	readonly name: string;

	/** Source of the image gallery to preview (to display turntable images) */
	readonly previewGallerySrc?: string[];

	/** Source of the image to preview (such as in property view) */
	readonly previewSrc?: string;

	/** Function to call to get new image data if/when the image is edited and replaced */
	readonly replaceWith?: (newImageNode: C8Node) => ImageData;

	/** Indicates if it is possible to have attributes on image (whether or not this user can edit) */
	readonly showAttributes?: boolean;

	/** Indicates if image replace action is available (only if referenced by image container or globally in image browser) */
	readonly showWhereUsed?: boolean;

	/** Thumbnail source of the image */
	readonly thumbnailSrc: string;

	/** Image node url */
	readonly url?: cnl;
}

/**
 * Get whether an image pixel total exceeds a given count by the sum of its height and width.
 */
export function doImagePixelsExceedCount(height: number, width: number, count: number): boolean {
	return (height + width) > count;
}

/**
 * Get whether an image exceeds a certain size defined by height or width.
 */
function doesImageExceedSize(height: number, width: number, size: number): boolean {
	return height > size || width > size;
}

/**
 * There is a defect file with the Chromium project concerning large images not displaying properly:
 * https://bugs.chromium.org/p/chromium/issues/detail?id=1234110
 *
 * In such a case, use the large image rather than the viewable image.
 */
function doesImageSizeExceedMax(height: number, width: number): boolean {
	return doesImageExceedSize(height, width, MAX_VIEWABLE_IMAGE_SIZE);
}

/**
 * There is a defect file with the Chromium project concerning large images not displaying properly:
 * https://bugs.chromium.org/p/chromium/issues/detail?id=1234110
 *
 * Use the large image in place of viewable if size exceeds the max allowed.
 */
function getAdjustedImageIdBasedOnMaxImageSize(imageNode: C8Node, imageId: string): string {
	return (imageId == ATTR_VIEWABLE) && doesImageSizeExceedMax(imageNode.Height, imageNode.Width) ? ATTR_LARGE_IMAGE : imageId;
}

/**
 * Return numbered key storing image for given image label
 */
export function convertImageLabelToNumberedKey(imageMap: C8ImageMap, label: string): string {
	const url = imageMap?.[label];
	if (url) {
		for (const key in imageMap) {
			const n = Number(key);

			if (n && imageMap[key] === url) {
				return key;
			}
		}
	}

	return null;
}

/**
 * Return default image for image container
 */
export function getDefaultImage(imageContainerNode: C8Node): cnl | undefined {
	return getImageContainerImage(imageContainerNode, DEFAULT_IMAGE_LABEL);
}

/**
 * Return attribute to use from image that is large enough to display the requested size
 */
export function getImageAttributeBySize(imageSize: string): string {
	return SIZE_ATTRIBUTE[imageSize];
}

/**
 * Return image in image container by key
 */
export function getImageContainerImage(imageContainerNode: C8Node, imageKey: string | number): cnl | undefined {
	const url = imageContainerNode?.[ATTR_IMAGES]?.[imageKey];
	return xml.isValidURL(url) ? url : undefined;
}

/**
 * Return numbered key of image container that references the given image
 */
export function getImageContainerNumberedKey(imageContainer: C8Node, imageUrl: cnl, containerAttributeId?: C8AttributeId): string {
	const value = imageContainer ? imageContainer[containerAttributeId || ATTR_IMAGES] : null;

	for (const key in value) {
		if (Number(key) && (value[key] === imageUrl)) {
			return key;
		}
	}

	return null;
}

/**
 * Return array of numbered keys for given image map, omitting the default key or given key if specified
 */
export function getImageContainerNumberedKeys(imageMap: C8ImageMap, bOmitDefault = false, omitKey?: string): string[] {
	const keys: string[] = [];

	if (imageMap) {
		const omitValue = bOmitDefault ? imageMap[DEFAULT_IMAGE_LABEL] : null;

		for (const key in imageMap) {
			const n = Number(key);
			if (n && key != omitKey && imageMap[key] != omitValue) {
				keys.push(key);
			}
		}
	}

	return _.sortBy(keys, Number);
}

/**
 * Return full enum value for the image label on the image container
 */
export function getImageLabelEnumValue(imageContainer: C8Node, key: string): string | undefined {
	const rc = getNodeClass(imageContainer);
	const rcEnum = rc.imageLabelEnum;
	return rcEnum?.getValue(key);
}

/**
 * Return maximum size of image based on attribute
 */
export function getImageSizeByAttribute(imageAttribute: C8AttributeId): number {
	if (imageAttribute === ATTR_THUMBNAIL) {
		return THUMBNAIL_IMAGE_SIZE;
	} else if (imageAttribute === ATTR_MEDIUM_IMAGE) {
		return SMALL_IMAGE_SIZE;
	}

	return LARGE_IMAGE_SIZE;
}

/**
 * Return image size enum to use based on given height and width - if both values are 0 then returns null
 */
export function getImageSizeByDimensions(height: number, width: number, bAllowOriginal = false): string | null {
	const x = Math.max(height, width / ASPECT_RATIO);
	if (x <= 0) {
		return null;
	}

	if (x <= SMALLEST_SIZE) {
		return IMAGE_SIZE_SMALLEST;
	} else if (x <= SMALL_SIZE) {
		return IMAGE_SIZE_SMALL;
	} else if (x <= MEDIUM_SIZE) {
		return IMAGE_SIZE_MEDIUM;
	} else if (x <= LARGE_SIZE) {
		return IMAGE_SIZE_LARGE;
	} else if (x <= LARGEST_SIZE) {
		return IMAGE_SIZE_LARGEST;
	} else if (x <= LARGE_IMAGE_SIZE) {
		return IMAGE_SIZE_USE_LARGE_IMAGE;
	}

	// some browsers cannot display many large files at once, so we want to limit the use of the viewable image to cases
	// where they are being viewed one-by-one rather than in gallery/table view
	return bAllowOriginal ? IMAGE_SIZE_ORIGINAL : IMAGE_SIZE_USE_LARGE_IMAGE;
}

/**
 * Return pixel height necessary to force image size based on the number of rows of images on the print page;
 * this could be more precisely calculated, but rule for now is to use LargeImage when the number of rows per page
 * is 1, and SmallImage otherwise.
 */
export function getImagePixelSizeByRowsPerPage(nRowsPerPage: number): number {
	return (nRowsPerPage == 1) ? LARGE_IMAGE_SIZE : LARGE_SIZE;
}

/**
 * Return image size enum to use based on given height and width - assume if both values are 0 then use largest size
 */
export function getImageSizeBySize(height: number, width: number, bAllowOriginal?: boolean): string {
	const x = Math.max(height, width / ASPECT_RATIO);
	if (x > 0) {
		if (x <= SMALLEST_SIZE) {
			return IMAGE_SIZE_SMALLEST;
		} else if (x <= SMALL_SIZE) {
			return IMAGE_SIZE_SMALL;
		} else if (x <= MEDIUM_SIZE) {
			return IMAGE_SIZE_MEDIUM;
		} else if (x <= LARGE_SIZE) {
			return IMAGE_SIZE_LARGE;
		} else if (x <= LARGEST_SIZE) {
			return IMAGE_SIZE_LARGEST;
		} else if (x <= LARGE_IMAGE_SIZE) {
			return IMAGE_SIZE_USE_LARGE_IMAGE;
		}
	}

	// some browsers cannot display many large files at once, so we want to limit the use of the viewable image to cases
	// where they are being viewed one-by-one rather than in gallery/table view
	return bAllowOriginal ? IMAGE_SIZE_ORIGINAL : IMAGE_SIZE_USE_LARGE_IMAGE;
}

/**
 * Return the max height allowed by the given image size (0 is returned if there is no known max size)
 */
export function getImageSizeMaxHeight(imageSize: string): number {
	return MAX_HEIGHT[imageSize] || 0;
}

/**
 * Return image src for given parameters
 * @param node - node where to start looking for image, which could itself be an Image node
 * @param pathToImageContainer - optional path to image container from given node
 * @param imageId - attribute storing version of image to return (Thumbnail, SmallImage, LargeImage, Viewable)
 * @param imageKey - optional image key/label to use
 */
export function getImageSrc(node: C8Node, pathToImageContainer?: string, imageId = ATTR_THUMBNAIL, imageKey = DEFAULT_IMAGE_LABEL): string | null {
	if (!node) {
		return null;
	}

	let src: string;

	if (node.$Type == TYPE_IMAGE) {
		imageId = getAdjustedImageIdBasedOnMaxImageSize(node, imageId);
		src = comm.getNodeFileHref(node.$URL, null, imageId, null, true);
	} else if (pathToImageContainer) {
		src = comm.getNodeFileHref(node.$URL, pathCreate(pathToImageContainer, ATTR_IMAGES, PATH_OP_INDEX, imageKey), imageId);
	} else if (node.Images) {
		// handle classes derived from ImageContainer
		src = comm.getNodeFileHref(node.Images[imageKey], null, imageId);
	} else if (node.Image) {
		// handle user (and possibly others)
		src = comm.getNodeFileHref(node.Image, null, imageId);
	} 

	// add pt time stamp to image to force refresh new images
	return src ? src + "&pt=" + node.$PT : null;
}


/**
 * Return image src for given parameters
 */
export function getNodeAttributeImageSrc(node: C8Node, attributeId: C8AttributeId, imageId = ATTR_THUMBNAIL): string | null {
	const href = comm.getNodeFileHref(node[attributeId], null, imageId);
	return href ? `${href}&pt=${node.$PT}` : null;
}

/**
 * Return maximum display size of image based on the given image size enum
 */
export function getSizeByImageSize(imageSize: string): ImageDimensions {
	const height = getImageSizeMaxHeight(imageSize) || SMALLEST_SIZE;
	return {
		height,
		width: height * ASPECT_RATIO,
	};
}

/**
 * Determine if given image container node has a default image
 */
export function hasDefaultImage(imageContainer: C8Node): boolean {
	return xml.isValidURL(imageContainer[ATTR_IMAGES][DEFAULT_IMAGE_LABEL]);
}

/**
 * Return true if given node is an ImageContainer
 */
export function isImageContainer(node: C8Node, attributeId?: C8AttributeId): boolean {
	return getNodeClass(node)?.isImageContainer && (!attributeId || (attributeId === ATTR_IMAGES));
}

/**
 * Return true if given key refers to an image label (does not include the default image label)
 */
export function isImageLabelKey(key: Key): boolean {
	const { id, index } = key;
	return (id === ATTR_IMAGES) && index && isNaN(Number(index));
}

/**
 * Return true if image label still exists for given image definition
 */
export function isImageLabelValid(imageOwnerClass: ReflectionClass, label: string): boolean {
	return label && imageOwnerClass.imageLabelEnum?.hasActiveValue(label);
}

/**
 * Determine if given image node is a turntable
 * @param node - Image node
 */
export function isTurntableImage(node: C8Node): boolean {
	return node.GalleryType == GALLERY_TYPE_TURNTABLE;
}

/**
 * Return list of img sources corresponding to turntable
 * @param individualImageId - desired size of image gallery based on individual image attribute (Thumbnail, SmallImage, LargeImage, Viewable) - defaults to SmallImage
 */
export function getImageGallerySrc(imageNode: C8Node, individualImageId?: string): string[] | undefined {
	const adjustedImageId = getAdjustedImageIdBasedOnMaxImageSize(imageNode, individualImageId || ATTR_MEDIUM_IMAGE);
	const galleryImageId = getImageGalleryAttributeByImageId(adjustedImageId);

	// there is no gallery for thumbnail images
	if (!galleryImageId) {
		return undefined;
	}

	return imageNode[galleryImageId].map((url: cnl) => {
		const params = {
			URL: url,
			Download: true,
		};

		return new GetRequest("Publisher", "GetDirect", params).toRequest() + "&pt=" + imageNode.$PT;
	});
}

/**
 * Return image gallery attribute given an image id
 */
export function getImageGalleryAttributeByImageId(imageId: string): string {
	if (imageId === ATTR_VIEWABLE) {
		return ATTR_VIEWABLE_GALLERY;
	} else if (imageId === ATTR_MEDIUM_IMAGE) {
		return ATTR_MEDIUM_GALLERY;
	} else if (imageId === ATTR_LARGE_IMAGE) {
		return ATTR_LARGE_GALLERY;
	} else {
		return "";
	}
}

/**
 * Return an ImageData structure given an Image node and an optional ImageContainer node
 */
export function getImageData(
	imageNode: C8Node,
	containerNode?: C8Node,
	containerAttributeId?: C8AttributeId,
	imageKey?: string,
	previewImageId?: C8AttributeId,
	fixedImageLabel?: string,
	globalContext?: boolean,
): ImageData {
	if (!imageNode) {
		return null;
	}

	// create functions to return image data when actions are performed on the image
	const replaceWith = (newImageNode: C8Node): ImageData => getImageData(
		newImageNode,
		containerNode,
		containerAttributeId,
		imageKey,
		previewImageId,
		fixedImageLabel,
		globalContext,
	);

	const copyTo = (newImageNode: C8Node, newImageContainerNode: C8Node): ImageData => getImageData(
		newImageNode,
		newImageContainerNode,
		containerAttributeId,
		getImageContainerNumberedKey(newImageContainerNode, newImageNode.$URL, containerAttributeId),
		previewImageId,
		null,
		globalContext,
	);

	const {
		$CT,
		$Name,
		$URL,
		FileSize,
		Height,
		Width,
	} = imageNode;

	previewImageId ||= ATTR_MEDIUM_IMAGE;

	const isTurntable = isTurntableImage(imageNode);
	const lightboxGallerySrc = isTurntable ? getImageGallerySrc(imageNode, ATTR_VIEWABLE) : null;
	const previewGallerySrc = isTurntable ? getImageGallerySrc(imageNode, previewImageId) : null;
	let showAttributes = !!globalContext;
	let showWhereUsed = showAttributes;
	let editableAttributes = showAttributes;
	let editableImage = showAttributes;
	let isDefault = false;
	let imageLabels: string[];
	let editableAttributeKey: string;

	if (fixedImageLabel) {
		// only display single labeled image from ImageContainer
		imageLabels = [fixedImageLabel];
		isDefault = fixedImageLabel === DEFAULT_IMAGE_LABEL;
		showAttributes = true;
		showWhereUsed = true;
		editableAttributes = true;
		editableImage = true;
	} else if (isImageContainer(containerNode, containerAttributeId)) {
		// extract imageLabels and isDefault from ImageContainer
		const defaultImageUrl = getDefaultImage(containerNode);
		const imageUrl = getImageContainerImage(containerNode, imageKey);
		containerAttributeId = ATTR_IMAGES;
		imageLabels = getImageLabeledKeys(containerNode, imageKey, false, true);
		isDefault = defaultImageUrl === imageUrl;
		showAttributes = true;
		showWhereUsed = true;
		editableAttributes = true;
		editableImage = true;
	} else if (containerNode) {
		showAttributes = reflectionHints.doesImageContainerAllowAttributes(containerNode.$Type);
		editableAttributeKey = imageKey;
		editableAttributes = false;
		editableImage = reflectionHints.doesImageContainerAllowEdit(containerNode.$Type);
	}

	// no editing allow unless have edit access to container
	if (containerNode && !xml.nodeHasAccess(containerNode, xml.RIGHT_WRITE, containerAttributeId)) {
		editableAttributes = editableImage = false;
	}

	if (isTurntable || doesImageSizeExceedMax(Height, Width)) {
		editableImage = false;
	}

	return {
		containerUrl: containerNode?.$URL,
		containerAttributeId,
		copyTo,
		dateCreated: formatTime($CT, FORMAT_DATETIME),
		downloadUrl: imageNode[ATTR_VIEWABLE],
		editableAttributeKey,
		editableAttributes,
		editableImage,
		height: Height,
		imageKey: imageKey || DEFAULT_IMAGE_KEY,
		imageLabels,
		imageSize: formatFileSize(FileSize),
		imageType: getFileNameExtension($Name),
		isDefault,
		isTurntable,
		lightboxGallerySrc,
		lightboxSrc: getImageSrc(imageNode, null, ATTR_VIEWABLE),
		name: $Name,
		previewGallerySrc,
		previewSrc: getImageSrc(imageNode, null, previewImageId),
		replaceWith,
		showAttributes,
		showWhereUsed,
		thumbnailSrc: getImageSrc(imageNode, null, ATTR_THUMBNAIL),
		url: $URL,
		width: Width,
	};
}

/**
 * Return labeled keys in Images map for an ImageContainer node
 * @param includeDefault - indicates if default key should be considered as a labeled key
 * @param includeEnumPrefix - indicates if label should be returned as it is in the map (EnumValue), or as full enum value (EnumList:EnumValue)
 */
export function getImageLabeledKeys(
	imageContainerNode: C8Node,
	imageKey?: string,
	includeDefault?: boolean,
	includeEnumPrefix?: boolean,
): string[] {
	const imagesMap = imageContainerNode[ATTR_IMAGES];
	const targetImageUrl = imagesMap[imageKey];
	const keys: string[] = [];

	for (const key in imagesMap) {
		if (!Number(key) && (includeDefault || key)) {
			if ((targetImageUrl == null) || (imagesMap[key] === targetImageUrl)) {
				keys.push(key);
			}
		}
	}

	if (includeEnumPrefix && keys.length) {
		const rc = getNodeClass(imageContainerNode);
		const rcEnum = rc.imageLabelEnum;
		if (rcEnum) {
			return keys.map((key) => rcEnum.getValue(key));
		}
	}

	return keys;
}

/**
 * Return index of key in list of image data
 */
export function getIndexOfKeyInImageData(images: ImageData[], targetImageKey: string): number {
	const index = images.findIndex(({ imageKey }) => imageKey === targetImageKey);
	return Math.max(index, 0);
}
